import { useEffect, useState } from "react";
import { useTenant } from "../../Contexts/TenantContext";
import { useCart } from "../../Contexts/CartContext";
import { productService } from "../../Api/Services/productService";
import { SimplifiedAdminProductInStore } from "../../Models/Product";
import { formatPriceBRL } from "../../Utils/DataFormatters";
import { FaMinus, FaPlus, FaShoppingBag, FaTrash } from "react-icons/fa";
import React from "react";
import QuantitySelector from "../QuantitySelector/QuantitySelector";
import Button from "../Button/Button";

const ProductStep = () => {
  const { tenantPreference } = useTenant();
  const { cartItems, addToCart, clearCart, removeFromCart } = useCart();
  
  const enableProductComponents = tenantPreference?.enableProductComponents ?? true;
  const autoGenerateProductCode = tenantPreference?.autoGenerateProductCode ?? false;

  const SIZES_STANDARD = ["P", "M", "G", "GG"];
  const SIZES_PLUS = ["G1", "G2", "G3"];
  const ALL_SIZES = [...SIZES_STANDARD, ...SIZES_PLUS];

  const [products, setProducts] = useState<SimplifiedAdminProductInStore[]>([]);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [selectedSizes, setSelectedSizes] = useState<Record<string, string>>({});

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enableProductComponents]);

  async function load() {
    const res = await productService.getAllForInStore({ page: 1, pageSize: 100 });
    setProducts(res.items);

    const nextQty: Record<string, number> = {};
    const nextExpanded: Record<string, boolean> = {};

    // Initialize quantities as 1 for each product + component
    res.items.forEach(p => {
      nextQty[p.id] = 1;

      let shouldExpand = false;

      if (enableProductComponents) {
        p.components?.forEach(c => {
          const cid = c.component.id;
          nextQty[cid] = 1;

          if (cartItems.some(i => i.id === cid)) {
            shouldExpand = true;
          }
        });
      }

      nextExpanded[p.id] = shouldExpand;
    });

    // Merge cart quantities
    cartItems.forEach(item => {
      nextQty[item.id] = item.quantity ?? 1;
    });

    setQuantities(nextQty);
    setExpanded(nextExpanded);
  }

  function resetAllQuantities() {
    setQuantities(q => {
      const next: Record<string, number> = {};
      products.forEach(p => {
        next[p.id] = 1;
        if (enableProductComponents) {
          p.components?.forEach(c => {
            next[c.component.id] = 1;
          });
        }
      });
      return next;
    });
  }

  const toggleRow = (id: string) =>
    setExpanded(e => ({ ...e, [id]: !e[id] }));

  const getQty = (id: string) => quantities[id] ?? 1;

  const setQty = (id: string, val: number) =>
    setQuantities(q => ({ ...q, [id]: val }));

  const isInCart = (id: string, size?: string) =>
    size
      ? cartItems.some(i => i.id === id && i.size === size)
      : cartItems.some(i => i.id === id);

  return (
    <div className={`flex flex-col bg-white border p-4 flex-[1.6] rounded shadow`}>
      <div className="flex flex-col sm:flex-row items-center justify-between pb-5 border-b">
        <div className="flex flex-col gap-1 mb-4 sm:mb-5">
          <div className="flex items-center gap-2">
            <FaShoppingBag className="text-primary-600" />
            <h4 className="text-md font-semibold">{"PRODUTOS"}</h4>
          </div>
          <p className="text-xs text-gray-500">{"Produtos opcionais. Você pode avançar sem selecionar produtos e informar o valor manualmente."}</p>
        </div>

        {cartItems.length > 0 && (
          <Button variant="secondary"
            className="w-full sm:w-auto flex items-center justify-center text-xs gap-1 py-3 sm:py-2 px-4"
            onClick={() => {
              clearCart();
              resetAllQuantities();
            }}
          >
            <FaTrash />
            {"REMOVER TODOS OS PRODUTOS"}
          </Button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto">
        <table className={`w-full border-collapse border text-sm mt-6 border-gray-300`}>
          <thead>
            <tr className="bg-primary-120">
              {enableProductComponents && <th className="border p-3 text-center"></th>}
              <th className="border p-3 text-center hidden sm:table-cell">{"Imagem"}</th>
              {!autoGenerateProductCode && <th className="border p-3 text-center hidden sm:table-cell">{"Código"}</th>}
              <th className="border p-3 text-left">{"Nome"}</th>
              <th className="border p-3 text-center">{"Preço"}</th>
              <th className="border p-3 text-center hidden sm:table-cell">{"Estoque"}</th>
              <th className="border p-3 text-center hidden sm:table-cell">{"Qtd"}</th>
              {!enableProductComponents && <th className="border p-3 text-center">{"Tamanhos"}</th>}
              <th className="border p-3 text-center">{"Ações"}</th>
            </tr>
          </thead>

          <tbody>
            {products.map((product, idx) => {
              const qty = getQty(product.id);
              return (
                <React.Fragment key={product.id}>
                  {/* Product Row */}
                  <tr
                    key={idx}
                    className={`
                    hover:bg-gray-50
                      ${enableProductComponents && product.components?.length > 0 ? "cursor-pointer" : "cursor-default"}
                    `}
                    onClick={() => {
                      if (enableProductComponents && product.components?.length > 0) {
                        toggleRow(product.id);
                      }
                    }}
                  >
                    {enableProductComponents && (
                      <td className="border p-3 text-center">
                        {product.components?.length > 0 && (
                          <button
                            className="transition-opacity hover:opacity-70"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleRow(product.id);
                            }}
                          >
                            {expanded[product.id] ? (
                              <FaMinus className="text-primary-600 text-xs" />
                            ) : (
                              <FaPlus className="text-primary-600 text-xs" />
                            )}
                          </button>
                        )}
                      </td>
                    )}
                    <td className="border p-3 text-center hidden sm:table-cell">
                      <div className="w-12 h-12 flex items-center justify-center mx-auto">
                        {product.mainImageUrl ? (
                          <img
                            src={product.mainImageUrl}
                            alt={product.name}
                            className="w-12 h-12 object-cover rounded"
                          />
                        ) : (
                          <div className="w-12 h-12 flex items-center justify-center bg-gray-100 text-gray-400 rounded text-xs">
                            {"Sem imagem"}
                          </div>
                        )}
                      </div>
                    </td>
                    {!autoGenerateProductCode && (
                      <td className="border p-3 text-center hidden sm:table-cell">
                        {product.code}
                      </td>
                    )}
                    <td className="border p-3 text-left truncate">
                      {product.name}
                    </td>
                    <td className="border p-3 text-center">
                      {!enableProductComponents ? (() => {
                        const selectedSz = selectedSizes[product.id];
                        const isPlusSelected = selectedSz != null && SIZES_PLUS.includes(selectedSz);
                        const effectivePrice = isPlusSelected && product.plusPrice != null ? product.plusPrice : product.price;
                        return (
                          <span className="text-xs">
                            {formatPriceBRL(effectivePrice)}
                            {product.plusPrice != null && selectedSz == null && (
                              <><br /><span className="text-gray-500">{formatPriceBRL(product.plusPrice)}</span></>
                            )}
                          </span>
                        );
                      })() : formatPriceBRL(product.price)}
                    </td>
                    <td className="border p-3 text-center hidden sm:table-cell">
                      {product.stockQuantity}
                    </td>
                    <td className="border p-3 text-center hidden sm:table-cell">
                      <QuantitySelector
                        value={qty}
                        min={1}
                        max={product.stockQuantity > 0 ? product.stockQuantity : 99}
                        onChange={val => setQty(product.id, val)}
                        align="center"
                        disabled={!enableProductComponents ? false : isInCart(product.id)}
                      />
                    </td>
                    {!enableProductComponents && (
                      <td className="border p-3 text-center" onClick={e => e.stopPropagation()}>
                        <div className="flex flex-wrap gap-1 justify-center">
                          {(product.availableSizes?.length ? product.availableSizes : ALL_SIZES).map(sz => {
                            const inCart = isInCart(product.id, sz);
                            const isSelected = selectedSizes[product.id] === sz;
                            return (
                              <button
                                key={sz}
                                onClick={() => {
                                  setSelectedSizes(s => {
                                    if (s[product.id] === sz) {
                                      const next = { ...s };
                                      delete next[product.id];
                                      return next;
                                    }
                                    return { ...s, [product.id]: sz };
                                  });
                                }}
                                className={`px-2 py-0.5 rounded border text-xs font-semibold transition-colors
                                  ${inCart
                                    ? "bg-gray-200 text-gray-500 border-gray-300"
                                    : isSelected
                                      ? "bg-primary-600 text-white border-primary-600"
                                      : "border-gray-400 text-gray-700 hover:bg-gray-100"}`}
                              >
                                {sz}
                              </button>
                            );
                          })}
                        </div>
                      </td>
                    )}
                    <td className="border p-3 text-center">
                      {!enableProductComponents ? (() => {
                        const selectedSz = selectedSizes[product.id];
                        const inCartWithSize = selectedSz != null && isInCart(product.id, selectedSz);
                        return (
                          <div onClick={e => e.stopPropagation()}>
                            {inCartWithSize ? (
                              <Button variant="muted"
                                className="px-3 py-1 text-xs min-w-24"
                                onClick={() => removeFromCart(`${product.id}:${selectedSz}`)}
                              >
                                {"Remover"}
                              </Button>
                            ) : (
                              <Button
                                className="px-3 py-1 text-xs min-w-24"
                                disabled={selectedSz == null}
                                onClick={() => {
                                  if (selectedSz == null) {
                                    return;
                                  }
                                  const isPlusSize = SIZES_PLUS.includes(selectedSz);
                                  const effectivePrice = isPlusSize && product.plusPrice != null ? product.plusPrice : product.price;
                                  addToCart(
                                    {
                                      id: product.id,
                                      name: product.name,
                                      code: product.code,
                                      price: effectivePrice,
                                      image: product.mainImageUrl,
                                      stockQuantity: product.stockQuantity,
                                      origin: product.origin,
                                      isMadeToOrder: product.isMadeToOrder,
                                      quantity: qty,
                                      size: selectedSz,
                                      plusPrice: product.plusPrice ?? undefined
                                    },
                                    { message: `${product.name} (${selectedSz}) adicionado ao pedido!` }
                                  );
                                }}
                              >
                                {"Adicionar"}
                              </Button>
                            )}
                          </div>
                        );
                      })() : !isInCart(product.id) ? (
                        <Button
                          className="px-3 py-1 text-xs min-w-24"
                          onClick={(e) => {
                            e.stopPropagation();
                            addToCart(
                              {
                                id: product.id,
                                name: product.name,
                                code: product.code,
                                price: product.price,
                                image: product.mainImageUrl,
                                stockQuantity: product.stockQuantity,
                                origin: product.origin,
                                isMadeToOrder: product.isMadeToOrder,
                                quantity: qty
                              },
                              { message: `${product.name} adicionado ao pedido!` }
                            );
                          }}
                        >
                          {"Adicionar"}
                        </Button>
                      ) : (
                        <Button variant="muted"
                          className="px-3 py-1 text-xs min-w-24"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeFromCart(product.id);
                            setQty(product.id, 1);
                          }}
                        >
                          {"Remover"}
                        </Button>
                      )}
                    </td>
                  </tr>

                  {/* Components */}
                  {enableProductComponents &&
                    expanded[product.id] &&
                    product.components?.map(c => {
                      const comp = c.component;
                      const cqty = getQty(comp.id);
                      const cinCart = isInCart(comp.id);

                      return (
                        <tr
                          key={c.id}
                          className="bg-gray-50"
                        >     
                          <td className="border px-3 text-center text-primary-600 font-semibold text-xl">
                            ↳
                          </td>
                          <td className="border px-3 text-center hidden sm:table-cell">
                            <div className="w-10 h-10 flex items-center justify-center mx-auto">
                              {comp.mainImageUrl ? (
                                <img
                                  src={comp.mainImageUrl}
                                  alt={comp.name}
                                  className="w-10 h-10 object-cover rounded"
                                />
                              ) : (
                                <div className="w-10 h-10 flex items-center justify-center bg-gray-100 text-gray-400 rounded text-xs">
                                  {"Sem imagem"}
                                </div>
                              )}
                            </div>
                          </td>
                          {!autoGenerateProductCode && (
                            <td className="border px-3 text-center hidden sm:table-cell">
                              {comp.code}
                            </td>
                          )}
                          <td className="border px-3 text-left truncate">
                            {comp.name}
                          </td>
                          <td className="border px-3 text-center">
                            {formatPriceBRL(comp.price)}
                          </td>
                          <td className="border px-3 text-center hidden sm:table-cell">
                            {comp.stockQuantity}
                          </td>
                          <td className="border px-3 text-center hidden sm:table-cell">
                            <QuantitySelector
                              value={cqty}
                              min={1}
                              max={comp.stockQuantity > 0 ? comp.stockQuantity : 99}
                              onChange={val => setQty(comp.id, val)}
                              disabled={isInCart(comp.id)}
                            />
                          </td>
                          <td className="border px-3 text-center">
                            {!cinCart ? (
                              <Button
                                className="px-3 py-1 text-xs min-w-24"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  addToCart(
                                    {
                                      id: comp.id,
                                      name: comp.name,
                                      code: comp.code,
                                      price: comp.price,
                                      image: comp.mainImageUrl,
                                      stockQuantity: comp.stockQuantity,
                                      origin: comp.origin,
                                      isMadeToOrder: comp.isMadeToOrder,
                                      quantity: cqty
                                    },
                                    { message: `${comp.name} adicionado ao pedido!` }
                                  )
                                }}
                              >
                                {"Adicionar"}
                              </Button>
                            ) : (
                              <Button variant="muted"
                                className="px-3 py-1 text-xs min-w-24"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeFromCart(comp.id);
                                  setQty(comp.id, 1);
                                }}
                              >
                                {"Remover"}
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ProductStep;
